import { Card } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { formatNumberLocale } from "@/utils/helpers";
import { CompactFieldRow } from "./CompactFieldRow";
import Decimal from "decimal.js";

interface TenderCompactPricingCardProps {
  priceObject: PriceObject;
}

/**
 * TenderCompactPricingCard component renders tender pricing details in a compact format.
 * Uses looped field rendering for maintainability.
 *
 * @component
 * @example
 * <TenderCompactPricingCard priceObject={tenderPriceObject} />
 */
function TenderCompactPricingCard({ priceObject }: TenderCompactPricingCardProps) {
  const { t } = useTranslation(["common"]);
  const intl = useIntl();

  // Extract fields inline from priceObject
  const fields = [
    {
      key: "surcharge",
      label: t("pricing.labels.surcharge"),
      value: `${formatNumberLocale(intl, priceObject.surcharge ?? 0, "decimal", false, false, 0, 2)}%`,
      rawCopyValue: priceObject.surcharge ?? 0,
      formatType: "none" as const,
    },
    {
      key: "revenue",
      label: t("pricing.labels.totalrevenue"),
      value: priceObject.revenue ?? 0,
      formatType: "currency" as const,
    },
    {
      key: "pricePerHour",
      label: t("pricing.labels.totalpriceperhour"),
      value: priceObject.pricePerHour ?? 0,
      formatType: "currency" as const,
    },
    {
      key: "margin",
      label: t("pricing.labels.totalmargin"),
      value: `${formatNumberLocale(intl, priceObject.margin ?? 0, "decimal", false, false, 0, 2)}%`,
      rawCopyValue: priceObject.margin ?? 0,
      formatType: "none" as const,
    },
    {
      key: "monthlyRate",
      label: t("pricing.labels.monthlyrate"),
      value: priceObject.monthlyRate ?? 0,
      formatType: "currency" as const,
    },
    {
      key: "yearlyRate",
      label: t("pricing.labels.yearlyrate"),
      value: priceObject.yearlyRate ?? 0,
      formatType: "currency" as const,
    },
    {
      key: "employeeCount",
      label: t("pricing.labels.employeecount"),
      value: priceObject.totalEmployeeCount ?? 0,
      formatType: "decimal" as const,
    },
    {
      key: "hours",
      label: t("pricing.labels.hours"),
      value: new Decimal(priceObject.minutes?.cumulated ?? 0).dividedBy(60).toNumber(),
      formatType: "decimal" as const,
    },
  ];

  return (
    <Card className="p-3 space-y-2">
      <div className="space-y-1">
        {fields.map((field) => (
          <CompactFieldRow
            key={field.key}
            label={field.label}
            value={field.value}
            intl={intl}
            formatType={field.formatType}
            rawCopyValue={field.rawCopyValue}
          />
        ))}
      </div>
    </Card>
  );
}

export default TenderCompactPricingCard;
